import {
    BillingHistoryEntry,
    CreateSubscriptionPlanResponse,
    CreateSubscriptionResponse,
    PaginatedSubscriptionSearchResponse,
    ReplaceSubscriptionPlanResponse,
    SetDefaultPaymentMethodResponse,
    SetupIntentResponse,
    Subscription,
    SubscriptionPlan,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type {
    ChangeSubscriptionRequest,
    CreateSubscriptionRequest,
    ReplaceSubscriptionPlanRequest,
    SetDefaultPaymentMethodRequest,
    SubscriptionPlanWriteRequest,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import { createInstance, createPublicInstance } from 'src/api';
import { appConfig } from 'src/lib/config';
import { z } from 'zod';

/** Public (unauthenticated) axios instance for signup flows */
const publicAccessApi = createPublicInstance({ baseURL: appConfig.accessApi });

/** Authenticated axios instance for billing management */
const accessApi = createInstance({ baseURL: appConfig.accessApi }, { useLegacySerialization: false });

export async function getPlan(planId: string): Promise<z.infer<typeof SubscriptionPlan>> {
    const { data } = await publicAccessApi.get(`/subscriptions/plans/${planId}`);
    return SubscriptionPlan.parse(data);
}

export interface GetPlansOptions {
    inviteCode?: string;
    /** Superuser-only: include every plan, not just active/self-serve ones (for the admin panel). */
    includeAll?: boolean;
}

export async function getPlans(options: GetPlansOptions = {}): Promise<z.infer<typeof SubscriptionPlan>[]> {
    const { inviteCode, includeAll } = options;
    const params: { inviteCode?: string; includeAll?: boolean } = {};
    if (inviteCode) params.inviteCode = inviteCode;
    if (includeAll) params.includeAll = includeAll;

    // The endpoint also accepts anonymous requests, while an available token lets the
    // backend trim promotional stages to the user's remaining entitlement.
    const { data } = await accessApi.get('/subscriptions/plans', {
        params: Object.keys(params).length > 0 ? params : undefined,
    });
    return z.array(SubscriptionPlan).parse(data);
}

// =============================================================================
// Authenticated endpoints
// =============================================================================

export async function getMySubscriptions(): Promise<z.infer<typeof Subscription>[]> {
    const { data } = await accessApi.get('/subscriptions/me');
    return z.array(Subscription).parse(data);
}

export async function createSubscription(
    request: CreateSubscriptionRequest,
): Promise<z.infer<typeof CreateSubscriptionResponse>> {
    const { data } = await accessApi.post('/subscriptions', request);
    return CreateSubscriptionResponse.parse(data);
}

export async function cancelSubscription(id: string, immediately?: boolean): Promise<z.infer<typeof Subscription>> {
    const { data } = await accessApi.delete(`/subscriptions/${id}`, {
        params: immediately !== undefined ? { immediately } : undefined,
    });
    return Subscription.parse(data);
}

export async function reactivateSubscription(id: string): Promise<z.infer<typeof Subscription>> {
    const { data } = await accessApi.post(`/subscriptions/${id}/reactivate`);
    return Subscription.parse(data);
}

export async function changeSubscription(
    id: string,
    request: ChangeSubscriptionRequest,
): Promise<z.infer<typeof Subscription>> {
    const { data } = await accessApi.patch(`/subscriptions/${id}`, request);
    return Subscription.parse(data);
}

export async function getBillingHistory(limit?: number): Promise<z.infer<typeof BillingHistoryEntry>[]> {
    const { data } = await accessApi.get('/subscriptions/billing-history', {
        params: limit !== undefined ? { limit } : undefined,
    });
    return z.array(BillingHistoryEntry).parse(data);
}

export async function getSetupIntent(): Promise<z.infer<typeof SetupIntentResponse>> {
    const { data } = await accessApi.post('/subscriptions/setup-intent');
    return SetupIntentResponse.parse(data);
}

export async function setDefaultPaymentMethod(
    paymentMethodId: string,
): Promise<z.infer<typeof SetDefaultPaymentMethodResponse>> {
    const request: SetDefaultPaymentMethodRequest = { paymentMethodId };
    const { data } = await accessApi.post('/subscriptions/default-payment-method', request);
    return SetDefaultPaymentMethodResponse.parse(data);
}

/** GET /subscriptions/by-user/:userId — get subscriptions for a specific user (superuser only for other users) */
export async function getUserSubscriptions(userId: string): Promise<z.infer<typeof Subscription>[]> {
    const { data } = await accessApi.get(`/subscriptions/by-user/${userId}`);
    return z.array(Subscription).parse(data);
}

/** GET /subscriptions/search — search all subscriptions (superuser sees all, regular users see own) */
export async function searchSubscriptions(params?: {
    $search?: string;
    $skip?: number;
    $top?: number;
    $count?: boolean;
    $orderby?: string;
    $filter?: string;
}): Promise<z.infer<typeof PaginatedSubscriptionSearchResponse>> {
    const { data } = await accessApi.get('/subscriptions/search', {
        params: { ...params, $count: true },
    });
    return PaginatedSubscriptionSearchResponse.parse(data);
}

// =============================================================================
// Plan management (superuser)
// =============================================================================

/** POST /subscriptions/plans — create a new subscription plan (superuser). */
export async function createPlan(
    request: SubscriptionPlanWriteRequest,
): Promise<z.infer<typeof CreateSubscriptionPlanResponse>> {
    const { data } = await accessApi.post('/subscriptions/plans', request);
    return CreateSubscriptionPlanResponse.parse(data);
}

/**
 * PUT /subscriptions/plans/:planId — replace a plan in full (superuser).
 *
 * The backend rejects partial bodies (.strict()), so the request must include every plan
 * field. `existingSubscriptionBehavior` controls how live subscribers to the old plan are
 * handled: 'none' leaves them alone, 'period_end' schedules migration at period end,
 * 'immediate_prorated' migrates now with prorated billing.
 */
export async function replacePlan(
    planId: string,
    request: ReplaceSubscriptionPlanRequest,
): Promise<z.infer<typeof ReplaceSubscriptionPlanResponse>> {
    const { data } = await accessApi.put(`/subscriptions/plans/${planId}`, request);
    return ReplaceSubscriptionPlanResponse.parse(data);
}
